import { CANCEL_EDITING_REQUEST } from '../../shared/actions/shared.actions'
import { DISTANCE_UNIT } from '../../shared/i18n/i18n.types'
import { IndexedCollection, PayloadedAction } from '../../shared/types'
import { APPOINTMENT_DIALOG } from '../appointment/appointment.actions'
import { AUTH_NO_ROUTING } from '../auth/auth.actions'
import { AuthNoRoutingResponse, } from '../auth/auth.types'
import { IssueType, PaceSetterSituation } from '../issue/issue.types'
import { GeoAddress, GeoCoordinates } from '../location/geocode/types'
import { SET_BREAKDOWN_LOCATION } from '../location/location.actions'
import { CaptchaVersion, Vehicle } from '../member/member.types'
import events from '../tagging/events'
import { automatedVehicleAnalytics } from '../vehicle/vehicle.actions'
import {
  ACTIVE_STEP,
  ADD_TOWING_STEP,
  ANIMATE_SPLASHSCREEN_HIDE,
  CLEAR_FORCED_NEXT_ROUTE,
  CLOSE_CANCEL_FEEDBACK_MODAL,
  DIALOG_CLOSE,
  DIALOG_OPEN,
  DIALOG_RESET,
  DONE_STEP,
  EDIT_STEP,
  HIDE_SPLASHSCREEN,
  OPEN_CANCEL_FEEDBACK_MODAL,
  PENDING_STEP,
  REMOVE_VEHICLE_STEP,
  RESET_SKIP_ISSUE,
  SET_AB_TEST_PARAM,
  SET_CAPTCHA_MODAL_OPEN,
  SET_CAPTCHA_VERSION,
  SET_CHANNEL,
  SET_ORIGIN,
  SET_COUNTRY,
  SET_CURRENT_URL_AND_TITLE,
  SET_DISTANCE_UNIT,
  SET_FIRST_TIME_ACCESS,
  SET_FULL_MAP_BREAKDOWN_LOCATION,
  SET_IP_REGION_COORDS,
  SET_IS_COUPON_APPLIED,
  SET_ISSUE_TYPE,
  SET_MOCK_CAPTCHA,
  SET_PROBLEM_TOOLTIP,
  SET_PROD_ENV,
  SET_QUERY_PARAMS_VEHICLE_DATA,
  SET_REMOVE_CALL_AAA,
  SET_SPLASHSCREEN_STEP,
  SET_USER_DEFAULT_COORDS,
  SET_USER_DEFAULT_ZIPCODE,
  SET_WATCH_RECAPTCHA_POSITION,
  SHOW_SPLASHSCREEN,
  TOGGLE_GET_PROPOSITIONS,
  TRANSITION,
  USER_DEFAULT_COORDS_LOADED,
  SET_FORCED_NEXT_ROUTE,
  SET_ISSUE_LOADING_TIMER_CONFIG,
} from './ui.actions'
import { ABTestParams, Dialog, Origin, PageNavigationEvent, Step, StepStatus, StepTypes, VehicleSections, } from './ui.types'

export type ABTestsState = {
  [type in ABTestParams]: boolean
}

export interface SplashscreenState {
  visible: boolean
  step: 0 | 1 | 2 | 3
  animating: boolean
}

export interface UIState {
  steps: IndexedCollection<Step>
  dialog: {
    [type: string]: Dialog
  }
  appointmentDialog: boolean
  vehicleDetailsRedirect: boolean
  splashscreen: SplashscreenState
  isCouponApplied: boolean
  transitioning: boolean
  defaultUserCoords?: GeoAddress
  defaultUserCoordsLoading: boolean
  ipRegionCoords?: GeoCoordinates
  skipIssue?: boolean
  prodEnv: boolean
  showCancelFeedbackModal: boolean
  cancelFeedbackHasAvailableCall: boolean
  defaultZipCode?: string
  mockCaptcha: boolean
  country: string
  queryParamsVehicle: Vehicle
  isFirstTimeAccess: boolean,
  channel: string,
  origin: Origin | null,
  currentUrl: string,
  previousUrl: string,
  previousTitle: string,
  problemTooltip?: PaceSetterSituation,
  watchRecaptchaPosition?: boolean,
  forcedNextRoute: PageNavigationEvent | null,
  removeCallAaa: boolean,
  showFullMapBreakdownLocation: boolean,
  abTestsState: ABTestsState,
  distanceUnit: DISTANCE_UNIT,
  authResponse: AuthNoRoutingResponse,
  captchaVersion: CaptchaVersion,
  captchaModalOpen: boolean,
  issueType: IssueType,
  getPropositionsEnabled: boolean,
  issueLoadingTimerConfig: { t2Ms: number; t3Ms: number },
}

export const initialAbTestsState = Object.values(ABTestParams).reduce(
  (curr, key) => {
    curr[key] = false
    return curr
  },
  {} as ABTestsState
)

export const initialSteps = {
  1: {
    id: 1,
    url: StepTypes.BREAKDOWN_LOCATION,
    status: StepStatus.EDITING,
    title: 'Location',
    pageType: events.location.LOCATION_PAGE_TYPE,
  },
  2: {
    id: 2,
    url: StepTypes.VEHICLE,
    status: StepStatus.PENDING,
    title: 'Vehicle',
    sections: [
      VehicleSections.MAKES,
      VehicleSections.MODELS,
      VehicleSections.DETAILS,
    ],
    pageType: events.vehicle.VEHICLE_PAGE_TYPE,
  },
  3: {
    id: 3,
    url: StepTypes.ISSUE,
    status: StepStatus.PENDING,
    title: 'Issue',
    afterEach: [automatedVehicleAnalytics()],
    pageType: events.issue.ISSUE_PAGE_TYPE,
  },
  5: {
    id: 5,
    url: StepTypes.SUBMIT,
    status: StepStatus.PENDING,
    title: 'Submit',
    pageType: events.submit.SUMMARY_PAGE_TYPE,
  },
}

export const towingStep = {
  id: 4,
  url: StepTypes.TOWING,
  status: StepStatus.PENDING,
  title: 'Tow To',
  pageType: events.towTo.DESTINATION_PAGE_TYPE,
}

export const initialState: UIState = {
  steps: initialSteps,
  dialog: {},
  appointmentDialog: false,
  vehicleDetailsRedirect: false,
  splashscreen: {
    visible: false,
    step: 1,
    animating: false,
  },
  isCouponApplied: false,
  transitioning: false,
  skipIssue: false,
  prodEnv: false,
  showCancelFeedbackModal: false,
  cancelFeedbackHasAvailableCall: false,
  defaultZipCode: null,
  defaultUserCoords: null,
  defaultUserCoordsLoading: false,
  ipRegionCoords: null,
  mockCaptcha: false,
  country: null,
  queryParamsVehicle: null,
  isFirstTimeAccess: false,
  channel: '',
  origin: null,
  currentUrl: '',
  previousUrl: '',
  previousTitle: '',
  problemTooltip: null,
  watchRecaptchaPosition: false,
  forcedNextRoute: null,
  removeCallAaa: false,
  showFullMapBreakdownLocation: true,
  abTestsState: initialAbTestsState,
  distanceUnit: null,
  authResponse: null,
  captchaVersion: CaptchaVersion.V3,
  captchaModalOpen: false,
  issueType: null,
  getPropositionsEnabled: true,
  issueLoadingTimerConfig: { t2Ms: 3000, t3Ms: 15000 },
}

const stepChangeMap = {
  [PENDING_STEP]: StepStatus.PENDING,
  [EDIT_STEP]: StepStatus.EDITING,
  [ACTIVE_STEP]: StepStatus.ACTIVE,
  [DONE_STEP]: StepStatus.DONE,
}

export function reducer(
  state: UIState | undefined = initialState,
  action: PayloadedAction
): UIState {
  switch (action.type) {
    case PENDING_STEP:
    case EDIT_STEP:
    case ACTIVE_STEP:
    case DONE_STEP:
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.payload.id]: {
            ...action.payload,
            status: stepChangeMap[action.type],
          },
        },
      }
    case DIALOG_OPEN.MESSAGE:
    case DIALOG_OPEN.ERROR:
    case DIALOG_OPEN.PROMPT:
      return {
        ...state,
        dialog: {
          ...state.dialog,
          [action.payload.type]: {
            ...action.payload,
            displayedAt: new Date(),
          },
        },
      }
    case DIALOG_CLOSE: {
      delete state.dialog[action.payload.type]
      return {
        ...state,
        dialog: {
          ...state.dialog,
        },
      }
    }
    case DIALOG_RESET: {
      return {
        ...state,
        dialog: {
          ...initialState.dialog,
        },
      }
    }
    case OPEN_CANCEL_FEEDBACK_MODAL:
      return {
        ...state,
        showCancelFeedbackModal: true,
        cancelFeedbackHasAvailableCall: action.payload.hasAvailableCall,
      }
    case CLOSE_CANCEL_FEEDBACK_MODAL:
      return {
        ...state,
        showCancelFeedbackModal: false,
      }
    case CANCEL_EDITING_REQUEST:
      return {
        ...state,
        isCouponApplied: initialState.isCouponApplied,
      }
    case SHOW_SPLASHSCREEN:
    case HIDE_SPLASHSCREEN:
      if (state.splashscreen.step === 0) {
        return state
      }

      return {
        ...state,
        splashscreen: {
          visible: action.type === SHOW_SPLASHSCREEN,
          step: action.type === SHOW_SPLASHSCREEN ? 1 : state.splashscreen.step,
          animating: false,
        },
      }
    case ANIMATE_SPLASHSCREEN_HIDE:
      if (state.splashscreen.step === 0) {
        return state
      }

      return {
        ...state,
        splashscreen: {
          ...state.splashscreen,
          animating: true,
        },
      }

    case SET_IS_COUPON_APPLIED:
      return {
        ...state,
        isCouponApplied: action.payload,
      }

    case SET_SPLASHSCREEN_STEP:
      if (state.splashscreen.step === 0) {
        return state
      }

      return {
        ...state,
        splashscreen: {
          ...state.splashscreen,
          step: action.payload,
        },
      }
    case TRANSITION.REQUEST:
    case TRANSITION.SUCCESS:
      return {
        ...state,
        transitioning: action.payload,
      }
    case SET_USER_DEFAULT_COORDS:
      return {
        ...state,
        defaultUserCoords: { ...action.payload, address: {} },
        defaultUserCoordsLoading: true,
      }
    case SET_IP_REGION_COORDS:
      return {
        ...state,
        ipRegionCoords: action.payload,
      }
    case SET_BREAKDOWN_LOCATION.SUCCESS:
      return {
        ...state,
        ipRegionCoords: null,
      }
    case USER_DEFAULT_COORDS_LOADED:
      return {
        ...state,
        defaultUserCoordsLoading: false,
      }
    case RESET_SKIP_ISSUE:
      return {
        ...state,
        skipIssue: false,
      }
    case ADD_TOWING_STEP:
      {
        // TODO refactor required. Move logic to an effect.

        // if towing is required reset steps and add the addtional step
        // if not towing step will be removed if is already set
        if (action.payload === true) {
          state = {
            ...state,
            steps: {
              ...state.steps,
              4: {
                ...towingStep,
                ...state.steps[4],
              },
              5: {
                ...initialSteps[5],
              },
            },
          }
        } else if (state.steps[4]) {
          delete state.steps[4]

          return {
            ...state,
            steps: {
              ...state.steps,
              5: {
                ...state.steps[5],
              },
            },
          }
        }
      }
      return state

    case REMOVE_VEHICLE_STEP:
      const vehicleStep = Object.values(state.steps).find(
        (step) => step.url === StepTypes.VEHICLE
      )
      if (vehicleStep) {
        delete state.steps[vehicleStep.id]
        return {
          ...state,
          steps: {
            ...state.steps,
          },
        }
      }
      return state

    case SET_PROD_ENV:
      return {
        ...state,
        prodEnv: true,
      }
    case SET_USER_DEFAULT_ZIPCODE:
      return {
        ...state,
        defaultZipCode: action.payload,
      }
    case SET_MOCK_CAPTCHA:
      return {
        ...state,
        mockCaptcha: action.payload,
      }
    case SET_CAPTCHA_MODAL_OPEN:
      return {
        ...state,
        captchaModalOpen: action.payload,
      }
    case SET_CAPTCHA_VERSION:
      return {
        ...state,
        captchaVersion: action.payload,
      }
    case SET_COUNTRY:
      return {
        ...state,
        country: action.payload,
      }
    case SET_QUERY_PARAMS_VEHICLE_DATA:
      return {
        ...state,
        queryParamsVehicle: action.payload,
      }
    case SET_FIRST_TIME_ACCESS:
      return {
        ...state,
        isFirstTimeAccess: action.payload,
      }
    case SET_CHANNEL:
      return {
        ...state,
        channel: action.payload,
      }
    case SET_ORIGIN:
      return {
        ...state,
        origin: action.payload,
      }
    case SET_CURRENT_URL_AND_TITLE:
      return {
        ...state,
        previousUrl: action.payload.previousUrl,
        previousTitle: action.payload.title,
        currentUrl: action.payload.url,
      }
    case SET_FORCED_NEXT_ROUTE:
      return {
        ...state,
        forcedNextRoute: action.payload,
      }
    case CLEAR_FORCED_NEXT_ROUTE:
      return {
        ...state,
        forcedNextRoute: null,
      }
    case SET_PROBLEM_TOOLTIP:
      return {
        ...state,
        problemTooltip: action.payload,
      }
    case SET_REMOVE_CALL_AAA:
      return {
        ...state,
        removeCallAaa: action.payload,
      }
    case SET_WATCH_RECAPTCHA_POSITION:
      return {
        ...state,
        watchRecaptchaPosition: action.payload,
      }
    case SET_FULL_MAP_BREAKDOWN_LOCATION:
      return {
        ...state,
        showFullMapBreakdownLocation: action.payload,
      }
    case SET_AB_TEST_PARAM:
      const [payloadKey, payloadValue] = action.payload
      if (payloadKey === ABTestParams.TURN_OFF) {
        return {
          ...state,
          abTestsState: {
            ...initialAbTestsState
          },
        }
      }
      return {
        ...state,
        abTestsState: {
          ...state.abTestsState,
          [payloadKey]: payloadValue ?? true,
        },
      }
    case SET_DISTANCE_UNIT:
      return {
        ...state,
        distanceUnit: action.payload,
      }
    case AUTH_NO_ROUTING:
      return {
        ...state,
        authResponse: action.payload
      }
    case SET_ISSUE_TYPE:
      return {
        ...state,
        issueType: action.payload,
      }
    case APPOINTMENT_DIALOG:
      return {
        ...state,
        appointmentDialog: action.payload,
      }
    case TOGGLE_GET_PROPOSITIONS:
      return {
        ...state,
        getPropositionsEnabled: action.payload,
      }
    case SET_ISSUE_LOADING_TIMER_CONFIG:
      return {
        ...state,
        issueLoadingTimerConfig: action.payload,
      }
    default:
      return state
  }
}
